import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layers, Download, Sparkles, ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeader = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const DEFAULT_PROGRAM_KEY = 'mosaic_default_crosswalk_program';

export default function StudentCrosswalkSuggestion({ sessionId }) {
  const [programs, setPrograms] = useState([]);
  const [programId, setProgramId] = useState(localStorage.getItem(DEFAULT_PROGRAM_KEY) || '');
  const [modality, setModality] = useState('reading');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    axios.get(`${API}/crosswalk/programs`, { headers: authHeader() })
      .then(r => setPrograms(r.data.programs || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    setLoading(true);
    setError(null);
    const params = { modality };
    if (programId) params.program_id = programId;
    axios.get(`${API}/crosswalk/for-student/${sessionId}`, { headers: authHeader(), params })
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [sessionId, programId, modality]);

  const handleProgramChange = (e) => {
    const newId = e.target.value;
    setProgramId(newId);
    if (newId) localStorage.setItem(DEFAULT_PROGRAM_KEY, newId);
  };

  if (!data) return null;
  const { context, crosswalk } = data;

  return (
    <div className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-blue-50 rounded-lg p-5" data-testid="student-crosswalk-suggestion">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="text-emerald-600" size={20} />
          <h3 className="text-lg font-bold text-gray-900">Today's Lesson Plan Suggestion</h3>
        </div>
        <button onClick={() => setExpanded(!expanded)} className="text-gray-500 hover:text-gray-700" data-testid="crosswalk-expand-toggle">
          <ChevronDown size={18} className={`transition-transform ${expanded ? '' : '-rotate-90'}`} />
        </button>
      </div>

      <p className="text-xs text-gray-600 mb-4">
        Based on <strong>{context.student_name}</strong>'s most recent WIDA level in <strong>{modality}</strong>
        (<strong>Level {context.wida_level} {context.wida_level_name}</strong> · composite {context.wida_level_float}),
        here's what to pair with your core curriculum today.
      </p>

      {/* Program & modality selectors */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1 block">Your Program</label>
          <select value={programId} onChange={handleProgramChange} data-testid="crosswalk-program-select" className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white">
            <option value="">— Select your core program —</option>
            {programs.map(p => <option key={p.id} value={p.id}>{p.name} ({p.publisher})</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1 block">Modality Focus</label>
          <select value={modality} onChange={e => setModality(e.target.value)} data-testid="crosswalk-modality-select" className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white">
            <option value="reading">Reading</option>
            <option value="listening">Listening</option>
            <option value="speaking">Speaking</option>
            <option value="writing">Writing</option>
          </select>
        </div>
      </div>

      {loading && <div className="text-sm text-gray-500">Loading suggestion…</div>}
      {error && <div className="text-sm text-red-600">{error}</div>}

      {!programId && !loading && (
        <div className="text-sm text-blue-700 bg-white border border-blue-200 rounded p-3">
          👉 Select your core program above to see unit-specific suggestions. Your choice is remembered for next time.
        </div>
      )}

      {expanded && crosswalk && !loading && (
        <div className="space-y-3" data-testid="crosswalk-suggestion-body">
          <div className="bg-white border border-gray-200 rounded-md p-3">
            <div className="text-xs uppercase tracking-wider text-blue-700 font-semibold mb-1.5 flex items-center gap-1">
              <Layers size={12}/> In {crosswalk.program.name} — Try These Units First
            </div>
            <ul className="text-sm space-y-1">
              {crosswalk.recommended_units.map(u => (
                <li key={u.id} className="text-gray-800" data-testid={`suggestion-unit-${u.id}`}>
                  <strong>{u.name}</strong>
                  <span className="text-gray-500 text-xs ml-1">— {u.theme || u.focus || u.essential_question || u.skills}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white border border-gray-200 rounded-md p-3">
            <div className="text-xs uppercase tracking-wider text-purple-700 font-semibold mb-1.5">
              Use These Sentence Frames ({crosswalk.wida_level_name} · Level {crosswalk.wida_level})
            </div>
            <ul className="space-y-1">
              {crosswalk.sentence_frames.map((f, i) => (
                <li key={i} className="text-sm p-2 bg-purple-50 border-l-4 border-purple-400 rounded-r font-mono text-gray-800" data-testid={`suggestion-frame-${i}`}>
                  {f}
                </li>
              ))}
            </ul>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-white border border-gray-200 rounded-md p-3">
              <div className="text-xs uppercase tracking-wider text-emerald-700 font-semibold mb-1">Quick Scaffold</div>
              <div className="text-xs text-gray-700">{crosswalk.quick_scaffold}</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-md p-3">
              <div className="text-xs uppercase tracking-wider text-amber-700 font-semibold mb-1">Vocab Focus</div>
              <div className="text-xs text-gray-700">{crosswalk.vocabulary_focus}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
